import { useState } from 'react';
import { createShareLink, type SharedCard, type SharedKind } from '@/lib/shareLinks';

interface Props {
  kind: SharedKind;
  title: string;
  subtitle?: string;
  text?: string;
  cards?: SharedCard[];
  onClose: () => void;
}

/**
 * Création d'un lien de partage sécurisé pour une note ou un jeu de cartes.
 * Le mot de passe est facultatif ; il est stocké sous forme de condensat.
 */
export default function ShareItemModal({ kind, title, subtitle, text, cards, onClose }: Props) {
  const [password, setPassword] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await createShareLink({ kind, title, subtitle, text, cards }, password);
      setLink(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Le partage a échoué.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copie impossible. Sélectionnez le lien manuellement.');
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/45" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md bg-background-50 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center flex-shrink-0">
              <i className="ri-share-forward-line"></i>
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-bold font-heading text-foreground-950">Partager</h3>
              <p className="text-[11px] text-foreground-500 truncate">
                {kind === 'note' ? 'Note' : 'Jeu de cartes'} · {title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
            title="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {!link ? (
          <>
            <p className="text-xs text-foreground-600 leading-relaxed mb-4">
              Le destinataire ouvre le lien sans créer de compte. Ajoutez un mot de passe pour le protéger.
            </p>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1.5">
              Mot de passe (facultatif)
            </label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ex. Math2026"
              className="w-full px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 mb-4"
            />
            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">{error}</p>
            )}
            <button
              onClick={create}
              disabled={busy}
              className="w-full h-10 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-link"></i>}
              {busy ? 'Création…' : 'Créer le lien sécurisé'}
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-foreground-600 leading-relaxed mb-3">
              Lien prêt. {password.trim() ? 'Il est protégé par votre mot de passe.' : 'Il est accessible à toute personne qui le possède.'}
            </p>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100 border border-background-200/60 mb-3">
              <i className="ri-link text-foreground-500 text-sm flex-shrink-0"></i>
              <p className="text-[11px] text-foreground-700 break-all flex-1">{link}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copy}
                className="flex-1 h-10 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 flex items-center justify-center gap-2"
              >
                <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copied ? 'Copié' : 'Copier le lien'}
              </button>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 px-4 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 flex items-center gap-1.5 no-underline"
              >
                <i className="ri-external-link-line"></i>
                Ouvrir
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}